import { useMemo } from 'preact/hooks';
import { useAtomValue } from 'jotai';

import {
    createSingleSelectParameter,
    createMultiSelectParameter,
    createTextParameter,
    createBooleanParameter
} from './parameterManagement';
import { LauncherResources } from './LauncherResources';
import config from './config';
import {useSearchParams} from "react-router-dom";

const NAME_PARAM = "name";
const NAME_PARAM_DEFAULT = "GDK-demo";
const PACKAGE_PARAM = "package";
const PACKAGE_PARAM_DEFAULT = "com.example";
const SAMPLE_CODE_PARAM = "sample-code";
const SAMPLE_CODE_PARAM_DEFAULT = true;

const TYPE_PARAM = "type";
const BUILD_TOOL_PARAM = "buildTool";
const CLOUDS_PARAM = "clouds";
const TEST_PARAM = "test";
const LANGUAGE_PARAM = "language";
const JAVA_VERSION_PARAM = "java-version";
const MICRONAUT_VERSION_PARAM = "micronaut-version";
const APPLICATION_VERSION_PARAM = "version";

const SERVICES_PARAM = "services";
const FEATURES_PARAM = "features";

const [nameState, useNameParam] = createTextParameter(NAME_PARAM, NAME_PARAM_DEFAULT);
const [packageState, usePackageParam] = createTextParameter(PACKAGE_PARAM, PACKAGE_PARAM_DEFAULT);
const [sampleCodeState, useSampleCodeParam] = createBooleanParameter(SAMPLE_CODE_PARAM, SAMPLE_CODE_PARAM_DEFAULT);

const [typeState, typeOptionsState, useTypeParam] =
    createSingleSelectParameter(TYPE_PARAM, config.launcherResources.getOptionValues(LauncherResources.TYPE_OPTION));
const [buildToolState, buildToolOptionsState, useBuildToolParam] =
    createSingleSelectParameter(BUILD_TOOL_PARAM, config.launcherResources.getOptionValues(LauncherResources.BUILD_TOOL_OPTION));
const [cloudsState, cloudsOptionsState, useCloudsParam] =
    createMultiSelectParameter(CLOUDS_PARAM, config.launcherResources.getOptionValues(LauncherResources.CLOUDS_OPTION));
const [testState, testOptionsState, useTestParam] =
    createSingleSelectParameter(TEST_PARAM, config.launcherResources.getOptionValues(LauncherResources.TEST_OPTION));
const [languageState, languageOptionsState, useLanguageParam] =
    createSingleSelectParameter(LANGUAGE_PARAM, config.launcherResources.getOptionValues(LauncherResources.LANGUAGE_OPTION));
const [javaVersionState, javaVersionOptionsState, useJavaVersionParam] =
    createSingleSelectParameter(JAVA_VERSION_PARAM, config.launcherResources.getOptionValues(LauncherResources.JAVA_VERSION_OPTION));
const [, micronautVersionOptionsState, useMicronautVersionParam] =
    createSingleSelectParameter(MICRONAUT_VERSION_PARAM, config.launcherResources.getOptionValues(LauncherResources.MICRONAUT_VERSION_OPTION));
const [, applicationVersionOptionsState, ] =
    createSingleSelectParameter(APPLICATION_VERSION_PARAM, config.launcherResources.getOptionValues(LauncherResources.APPLICATION_VERSION_OPTION));

const [servicesState, servicesOptionsState, useServicesParam] =
    createMultiSelectParameter(SERVICES_PARAM, config.launcherResources.getOptionValues(LauncherResources.SERVICES_OPTION));
const [, featuresOptionsState, useFeaturesPlain] =
    createMultiSelectParameter(FEATURES_PARAM, config.launcherResources.getOptionValues(LauncherResources.FEATURES_OPTION));

const useFeaturesParam = () => {
    let [type, , , typeLoading] = useTypeParam();
    let [selectedFeatures, setSelectedFeatures, allAvailableFeatures, featuresLoading] = useFeaturesPlain();
    const loading = featuresLoading || typeLoading;

    const availableFeatures = useMemo(() => {
        if (allAvailableFeatures === undefined) {
            return undefined;
        }

        return allAvailableFeatures.filter(f => f.supportedProjectTypes?.includes(type));
    }, [allAvailableFeatures, type]);

    const categoryMap = useMemo(() => {
        if (availableFeatures === undefined) {
            return {};
        }

        const categoryMap = {};
        for (let feature of availableFeatures) {
            if (!categoryMap[feature.category]) {
                categoryMap[feature.category] = [feature];
            } else {
                categoryMap[feature.category].push(feature);
            }
        }
        return categoryMap;
    }, [availableFeatures]);

    return [selectedFeatures, setSelectedFeatures, availableFeatures, categoryMap, loading];
}

const useAllParams = () => {
    const [features, , availableFeatures, , ] = useFeaturesParam();
    const values = {
        type: useAtomValue(typeState),
        name: useAtomValue(nameState),
        package_: useAtomValue(packageState),
        exampleCode: useAtomValue(sampleCodeState),
        clouds: useAtomValue(cloudsState),
        test: useAtomValue(testState),
        jdkVersion: useAtomValue(javaVersionState),
        language: useAtomValue(languageState),
        build: useAtomValue(buildToolState),
        services: useAtomValue(servicesState),
        features,
        availableFeatures,
    };
    return config.optionSelectionModifier(values);
};

const useParamsError = () => {
    const useStateError = (state) => {
        const loadableValue = useAtomValue(state);
        return loadableValue.state === 'hasError' && loadableValue.error;
    }

    const typeError = useStateError(typeOptionsState);
    const buildToolError = useStateError(buildToolOptionsState);
    const testError = useStateError(testOptionsState);
    const cloudsError = useStateError(cloudsOptionsState);
    const languageError = useStateError(languageOptionsState);
    const javaVersionError = useStateError(javaVersionOptionsState);
    const micronautVersionError = useStateError(micronautVersionOptionsState);
    const servicesError = useStateError(servicesOptionsState);
    const featuresError = useStateError(featuresOptionsState);

    return useMemo(() => {
        const error = typeError || buildToolError || testError || cloudsError || languageError || javaVersionError ||
                micronautVersionError || servicesError || featuresError;
        if (error) {
            console.log("[OPTIONS ERROR]", error);
            return new Error(`Could not load launcher options. Please check your internet connection, ` +
             `try reloading the page and contact support if necessary. Error message: "${error.message}"`);
        }
        return null;
    }, [
        typeError, buildToolError, testError, cloudsError, languageError, javaVersionError, micronautVersionError,
        servicesError, featuresError
    ]);
};

const useApplicationVersion = () => {
    let value = useAtomValue(applicationVersionOptionsState);
    return value.state === 'hasData' ? value.data.defaultValue : "...";
}

const useClearServicesAndFeaturesParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    return () => {
        searchParams.set(FEATURES_PARAM, "");
        searchParams.set(SERVICES_PARAM, "");
        setSearchParams(searchParams);
    };
}

export {
    useNameParam,
    usePackageParam,
    useSampleCodeParam,
    useTypeParam,
    useBuildToolParam,
    useCloudsParam,
    useTestParam,
    useLanguageParam,
    useJavaVersionParam,
    useMicronautVersionParam,
    useServicesParam,
    useApplicationVersion,
    useFeaturesParam,
    useAllParams,
    useParamsError,
    useClearServicesAndFeaturesParams
};
